<template>
  <Card class="tw-h-full tw-max-h-[290px]">
    <CardContent class="tw-pt-4">
      <CardTitle class="tw-uppercase tw-text-[0.8rem]">
        {{ t('auth.titles.reset_password') }}
      </CardTitle>
      <CardDescription>{{ t('auth.titles.password') }}</CardDescription>

      <div>
        <Button
          v-if="!isResetPasswordInputsShown"
          class="tw-w-full tw-mt-4"
          @click="showResetPasswordInputs"
        >
          {{ t('auth.titles.reset_password') }}
        </Button>

        <Form
          v-else
          :validation-schema="newPasswordSchema"
          v-slot="{ errors, isSubmitting }"
          @submit="onNewPasswordCreated"
        >
          <div class="tw-mb-4">
            <Field
              id="password"
              name="password"
              :type="'password'"
              :as="Input"
              :class="{ 'tw-border-danger': errors.password }"
              :placeholder="t('auth.titles.enter_password')"
            />

            <div v-if="errors.password" class="tw-text-danger tw-text-xs tw-mt-1">
              {{ errors.password }}
            </div>
          </div>

          <div class="tw-mb-4">
            <Field
              id="confirmPassword"
              name="confirmPassword"
              :type="'password'"
              :as="Input"
              :class="{ 'tw-border-danger': errors.confirmPassword }"
              :placeholder="t('auth.titles.confirm_password_placeholder')"
            />

            <div v-if="errors.confirmPassword" class="tw-text-danger tw-text-xs tw-mt-1">
              {{ errors.confirmPassword }}
            </div>
          </div>

          <div v-if="passwordResetSuccessState !== null">
            <span v-if="passwordResetSuccessState" class="tw-text-green-600 tw-text-[0.9rem]">
              {{ t('profile.titles.password_reset_success') }}
            </span>
            <span v-else class="tw-text-danger tw-text-[0.9rem]">Something went wrong.</span>
          </div>

          <Button class="tw-w-full tw-mt-4" :disabled="isSubmitting">
            {{ t('auth.titles.reset_password') }}
          </Button>
        </Form>
      </div>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { ref, type Ref } from 'vue';
import { useUserStore } from '@/stores/user.store';

import { Form, Field } from 'vee-validate';
import { object, string, ref as YupRef } from 'yup';
import { useI18n } from 'vue-i18n';
import { Card, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const { t } = useI18n();
const newPasswordSchema = object().shape({
  password: string()
    .required(t('auth.titles.enter_password_msg'))
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9\s]).{8,}$/,
      t('auth.forms.password_invalid')
    ),
  confirmPassword: string()
    .required(t('auth.titles.confirm_password_msg'))
    .oneOf([YupRef('password'), null, ''], t('auth.forms.passwords_not_match')),
});

const isResetPasswordInputsShown = ref(false);
const passwordResetSuccessState: Ref<null | boolean> = ref(null);
const { resetPassword } = useUserStore();

const showResetPasswordInputs = () => {
  isResetPasswordInputsShown.value = true;
};

const onNewPasswordCreated = async (values: any) => {
  try {
    await resetPassword(values.password, values.confirmPassword);

    passwordResetSuccessState.value = true;
  } catch (error) {
    console.log(error);
    passwordResetSuccessState.value = false;
  }
};
</script>

<style scoped>
/* Using Tailwind classes - no additional styles needed */
</style>
